import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError, isAxiosError } from 'axios';
import { formatInTimeZone } from 'date-fns-tz';

import { EvChargerData, GenericStatusResponse } from 'clipsal-cortex-types/src/api/api-evcharger';

import { RootState } from '../../../app/store';
import { NetworkError, patch, post } from '../../../common/api/api-helpers';
import { timeout } from '../../../utils/common/common-utils';
import {
  trackDynatraceAddEvseToSiteError,
  trackDynatraceAddEvseToSiteSuccess,
  trackDynatraceEvseModelSyncError,
} from '../../../utils/native/dynatrace-events';
import { addEvCharger, setCurrentSite } from '../../site/siteSlice';
import {
  addEnrolledEvChargerDetails,
  COMMISSIONING_STARTED_AT_STORAGE_KEY,
  setEvseVariant,
} from '../dashboard/evChargerSlice';
import { DetectedEvseModel, getEvseTypeForVariant, resolveEvseVariant } from '../evse-variant';
import { EvseType, QRCodeValues } from '../types';

const MANUFACTURER_ID_SCHNEIDER = 52;

// Right after leaving the EVSE AP the OS reports Wi-Fi as connected before the internet route is usable,
// so requests fail with a response-less "Network Error". Retry those with a growing delay.
const MODEL_SYNC_MAX_ATTEMPTS = 3;
const MODEL_SYNC_RETRY_DELAY_MS = 1000;

// Counts enrollment attempts across retries (DeviceScan resets enrollment state after a failure
// and lets the user rescan/retry). Reset to 0 on success so a future, unrelated enrollment starts fresh.
let enrollAttemptNumber = 0;

// Call this whenever a fresh onboarding funnel begins (e.g. LocationInfo mount) or on logout, so an
// abandoned/previous attempt's count doesn't leak into a later, unrelated enrollment's attempt_number.
export function resetEnrollAttemptCounter() {
  enrollAttemptNumber = 0;
}

export type DeviceSetupState = {
  qrCodeValues: QRCodeValues | null;
  status: GenericStatusResponse['status'];
  message: string;
  reason?: 'not-found' | 'charger-id-mismatch' | 'invalid-uuid';
  isLoaded: boolean;
  hasInitiatedEnroll: boolean;
  // Read from the charger over Soft-AP; kept until it is persisted to the backend.
  detectedEvseModel: DetectedEvseModel | null;
};

export type SiteDeviceEvseModelUpdate = {
  model_number: string;
  // Omitted when the model number does not resolve to a known variant.
  evse_type?: EvseType;
};

export const enrolEvCharger = createAsyncThunk<GenericStatusResponse, QRCodeValues, { state: RootState }>(
  'site/enrolEvCharger',
  async ({ sn: serialNumber, cpid: chargerId }, { getState, dispatch }) => {
    // Charger QR scanning gives the following attributes:
    // sn = Serial number of the charger
    // cpid = ID of the charger

    const state = getState();
    const siteID = state.site.site_id;
    const manufacturer = 'Schneider Electric';
    const body = {
      charger_id: chargerId,
      serial_number: serialNumber,
      manufacturer_name: manufacturer,
    };

    enrollAttemptNumber += 1;

    let response: EvChargerData;
    try {
      response = await post<EvChargerData>(`/v1/sites/${siteID}/ev_chargers`, body);
    } catch (error) {
      const errorResponse = ((<AxiosError>error) as NetworkError)?.response;
      trackDynatraceAddEvseToSiteError(
        errorResponse?.data?.detail || errorResponse?.data?.message || 'add_evse_to_site_failed',
        siteID,
        enrollAttemptNumber
      );

      if (errorResponse?.status === 404) {
        return { status: 'fail', message: errorResponse.data.detail || 'Charger not found', reason: 'not-found' };
      }

      if (errorResponse?.status === 409) {
        if (errorResponse.data.code?.includes('EV_CHARGER_ALREADY_IN_USE')) {
          return {
            status: 'fail',
            message: errorResponse.data.detail || 'Charger already in use',
            reason: 'charger-already-in-use',
          };
        }
        return {
          status: 'fail',
          message: errorResponse.data.detail || 'Charger ID mismatch',
          reason: 'charger-id-mismatch',
        };
      }

      if (errorResponse?.status === 400) {
        return { status: 'fail', message: errorResponse.data.detail || 'Invalid charger data', reason: 'invalid-uuid' };
      }

      const message = errorResponse?.data.message || '';
      return { status: 'fail', message };
    }

    // Post-success setup runs outside the POST's try/catch — if any of it throws, the thunk
    // should reject normally rather than emit a contradictory Add-EVSE failure for a call that
    // actually succeeded.
    trackDynatraceAddEvseToSiteSuccess(siteID, response.site_device_id);
    enrollAttemptNumber = 0;

    // Mark the site as being in onboarding so the tariff setup flow is shown after WiFi setup.
    // Dispatch before addEvCharger so setCurrentSite (which replaces all site state) doesn't
    // overwrite the device that addEvCharger adds next.
    dispatch(setCurrentSite({ ...state.site, isOnboarding: true }));

    // Add the charger to the site.
    dispatch(
      addEvCharger({
        id: response.site_device_id,
        device_type: 'EV_CHARGER',
        manufacturer_id: MANUFACTURER_ID_SCHNEIDER,
        manufacturer_name: manufacturer,
        manufacturer,
        model: '',
        created: formatInTimeZone(new Date(), state.site.timezone, 'yyyy-MM-dd'),
        site_identifier: chargerId,
        serial_number: serialNumber,
        operating_state: null,
        is_hybrid_inverter: false,
        ja12_compliance: false,
      })
    );

    // Need to pull fresh data from the server as the redux store is not updated yet.
    // Also set commissioningStartedAt to drive the temporary "Almost there..." UI until first SSE status arrives.
    const commissioningStartedAt = Date.now();
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COMMISSIONING_STARTED_AT_STORAGE_KEY, String(commissioningStartedAt));
    }
    dispatch(addEnrolledEvChargerDetails({ ...response, commissioningStartedAt }));

    return { status: 'success' };
  }
);

// A failed save is kept across restarts so it can be retried once the app is back online.
export const PENDING_EVSE_MODEL_SYNC_STORAGE_KEY = 'evse_pending_model_sync';

type PendingEvseModelSync = SiteDeviceEvseModelUpdate & { site_id: number; site_device_id: number | string };

function readPendingEvseModelSync(state: RootState): PendingEvseModelSync | null {
  try {
    const pending = JSON.parse(window.localStorage.getItem(PENDING_EVSE_MODEL_SYNC_STORAGE_KEY) ?? 'null');
    if (!pending?.model_number || pending.site_id !== state.site.site_id) return null;
    // Drop a save for a charger that is no longer on this site, otherwise it would be retried forever.
    const isOnSite = state.site.devices?.some((device) => String(device.id) === String(pending.site_device_id));
    if (!isOnSite) {
      window.localStorage.removeItem(PENDING_EVSE_MODEL_SYNC_STORAGE_KEY);
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

/**
 * Persists the model number and EVSE type detected over Soft-AP to the site device, or retries a previously
 * failed save. Must run once the phone is back on the internet. Resolves `true` when the backend was updated.
 */
export const syncDetectedEvseModel = createAsyncThunk<boolean, void, { state: RootState }>(
  'deviceSetup/syncDetectedEvseModel',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { deviceSetup, evCharger, site } = state;
    const detected = deviceSetup.detectedEvseModel;
    const evseType = detected ? getEvseTypeForVariant(detected.evseVariant) : null;
    const siteDeviceId =
      evCharger.siteDeviceId || site.devices?.find((device) => device.device_type === 'EV_CHARGER')?.id;

    const pending: PendingEvseModelSync | null =
      detected?.modelNumber && siteDeviceId
        ? {
            site_id: site.site_id,
            site_device_id: siteDeviceId,
            model_number: detected.modelNumber,
            ...(evseType && { evse_type: evseType }),
          }
        : readPendingEvseModelSync(state);

    if (!pending) return false;

    // Keeps the detected variant across restarts until the backend returns it as the device model.
    dispatch(setEvseVariant(resolveEvseVariant(pending.model_number)));

    const serializedPending = JSON.stringify(pending);
    window.localStorage.setItem(PENDING_EVSE_MODEL_SYNC_STORAGE_KEY, serializedPending);

    const { site_id: _siteId, site_device_id: pendingSiteDeviceId, ...body } = pending;
    for (let attempt = 1; ; attempt++) {
      try {
        await patch(`/v1/ev_chargers/${pendingSiteDeviceId}`, body);
        // A newer scan may have queued a different save meanwhile.
        if (window.localStorage.getItem(PENDING_EVSE_MODEL_SYNC_STORAGE_KEY) === serializedPending) {
          window.localStorage.removeItem(PENDING_EVSE_MODEL_SYNC_STORAGE_KEY);
        }
        return true;
      } catch (error) {
        const isNetworkUnreachable = isAxiosError(error) && !error.response;
        if (isNetworkUnreachable && attempt < MODEL_SYNC_MAX_ATTEMPTS) {
          await timeout(MODEL_SYNC_RETRY_DELAY_MS * attempt);
          continue;
        }
        trackDynatraceEvseModelSyncError(error instanceof Error ? error.message : String(error), pendingSiteDeviceId);
        throw error;
      }
    }
  }
);

const initialState: DeviceSetupState = {
  isLoaded: false,
  qrCodeValues: null,
  hasInitiatedEnroll: false,
  status: 'success',
  message: '',
  detectedEvseModel: null,
};

export const deviceSetupSlice = createSlice({
  name: 'deviceSetup',
  initialState,
  reducers: {
    resetEnrollState: (state) => {
      return { ...state, hasInitiatedEnroll: false };
    },
    setQRCodeValues: (state, action: PayloadAction<QRCodeValues | null>) => {
      state.qrCodeValues = action.payload;
    },
    setDetectedEvseModel: (state, action: PayloadAction<DetectedEvseModel | null>) => {
      state.detectedEvseModel = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(enrolEvCharger.pending, (state) => {
      return { ...state, hasInitiatedEnroll: true, isLoaded: false };
    });
    builder.addCase(enrolEvCharger.fulfilled, (state, { payload }) => {
      return { ...state, ...payload, isLoaded: true };
    });
    builder.addCase(enrolEvCharger.rejected, (state) => {
      return { ...state, status: 'fail', isLoaded: true };
    });
    builder.addCase(syncDetectedEvseModel.fulfilled, (state, { payload: isSynced }) => {
      if (isSynced) state.detectedEvseModel = null;
    });
  },
});

export const { resetEnrollState, setQRCodeValues, setDetectedEvseModel } = deviceSetupSlice.actions;

export const selectDeviceSetupState = (state: RootState) => {
  return state.deviceSetup;
};

export const { reducer: deviceSetupReducer } = deviceSetupSlice;
